import React, { useEffect, useMemo, useState } from 'react'
import { connect, useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'
import * as Sentry from '@sentry/react'
import { LDContext, LDMultiKindContext } from 'launchdarkly-js-client-sdk'
import { AnyAction, bindActionCreators, Dispatch } from 'redux'

import { PENDO_ENVIRONMENT_IDENTIFIER } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/pendoConstants'
import { useLDClient } from '@lyrahealth-inc/shared-app-logic/src/features/flags/launchdarkly-client-sdk.web'
import { launchDarklyUserDeviceUUID } from '@lyrahealth-inc/shared-app-logic/src/features/flags/launchDarklyUserDeviceUUID'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { LD_SHARED_CONTEXT_KEYS } from '@lyrahealth-inc/shared-app-logic/src/features/flags/utils'
import { useGetCustomerForClient } from '@lyrahealth-inc/shared-app-logic/src/features/providers/hooks/useGetCustomerForClient'
import { CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import { useFetcher } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useFetcher'

import useTwilioMessaging from './common/components/hooks/useTwilioMessaging'
import { LD_IDENTIFY_TIMEOUT_MS } from './common/utils/launchDarklyIdentification'
import { buildPendoProviderVisitor, resolvePendoDAMigrationEvaluation } from './common/utils/pendoProviderMetadata'
import { isEngageDAProvider } from './common/utils/utils'
import { useSetApiMetadata } from './data/apiPersist/hooks/useSetApiMetadata'
import { setLdInitialized } from './data/appSlice'
import {
  getAuthConfig,
  getAuthIsLoggedIn,
  getAuthMosApproved,
  getAuthRoles,
  getAuthSupervisor,
  getAuthSupervisorRoles,
  getAuthUser,
  getAuthUserCountry,
  getAuthUserId,
} from './data/auth/authSelectors'
import useWebKey from './data/hooks/useWebKey'
import { getRequestPaymentCustomersByCountry } from './data/requestPayment/requestPaymentSelectors'
import { useAppDispatch } from './data/store'
import { setScope } from './data/userSettingsSlice'
import { useProviderPendoVisitor } from './hooks/useProviderPendoVisitor'
import {
  buildLaunchDarklyClientContext,
  filterEpisodesForProvider,
  getEffectiveClientGenAiOptOut,
  shouldFetchAiCoachConversationEvidence,
} from './launchDarklyClientContext'
import { useCustomersForClient } from './lyraTherapy/clients/clientDetails/data/hooks/useCustomersForClient'
import { useSelectedClientData } from './lyraTherapy/clients/clientDetails/data/hooks/useSelectedClientData'
import { useAllEpisodesForSelectedClient } from './lyraTherapy/episodes/data/hooks/useAllEpisodesForSelectedClient'
import { usePatientConversationsData } from './lyraTherapy/messages/data/hooks/useConversationsData'
import { getProviderCapacity } from './providers/data/providersDataActions'
import { getCustomers, getCustomersByCountry } from './requestPayment/data/requestPaymentActions'

export const AppContainer: React.FC<AppContainerProps> = ({
  children,
  actions: { getCustomers, getProviderCapacity, getCustomersByCountry },
}) => {
  const isLoggedIn: boolean = useSelector(getAuthIsLoggedIn)
  const userId = useSelector(getAuthUserId)
  const user = useSelector(getAuthUser)
  const launchDarklyUserId = !userId ? LD_SHARED_CONTEXT_KEYS.LAUNCH_DARKLY_USER_UUID : userId
  const { data: clientDetails } = useSelectedClientData()
  const config = useSelector(getAuthConfig)
  const roles = useSelector(getAuthRoles)
  const country = useSelector(getAuthUserCountry)
  const mosApproved = useSelector(getAuthMosApproved)
  const supervisorRoles = useSelector(getAuthSupervisorRoles)
  const clientId = clientDetails?.id
  const clientCountry = clientDetails?.country
  const clientLanguage = clientDetails?.language
  const customersByCountry = useSelector(getRequestPaymentCustomersByCountry)
  const webKeyData = useWebKey()
  const supervisor = useSelector(getAuthSupervisor)
  const { isDAProviderMigrated, isGoogleCatalystLyraAiEnabled } = useFlags()
  const customersForClient = useCustomersForClient()
  const customer = useGetCustomerForClient(customersForClient, clientDetails)
  const { data: episodes } = useAllEpisodesForSelectedClient()
  const providerEpisodes = useMemo(() => filterEpisodesForProvider(episodes, userId), [episodes, userId])
  const shouldFetchAiCoachConversations = shouldFetchAiCoachConversationEvidence({
    episodes: providerEpisodes,
    genAiOptOut: customer?.gen_ai_opt_out,
    isGoogleCatalystLyraAiEnabled,
  })
  // This request must not depend on isAiCoachEnabled: its result helps determine that flag.
  // Limit it to clients that can qualify for the Catalyst override so ordinary client pages
  // do not make an unnecessary background request or surface an unrelated API error.
  const { currentData: aiCoachConversations } = usePatientConversationsData(
    clientDetails && userId && shouldFetchAiCoachConversations
      ? {
          patientId: clientDetails.id,
          providerId: userId,
          types: CONVERSATION_TYPE.AI_COACH,
        }
      : skipToken,
  )
  const effectiveClientGenAiOptOut = useMemo(
    () =>
      getEffectiveClientGenAiOptOut({
        genAiOptOut: customer?.gen_ai_opt_out,
        conversations: aiCoachConversations,
        episodes: providerEpisodes,
        isGoogleCatalystLyraAiEnabled,
      }),
    [aiCoachConversations, customer?.gen_ai_opt_out, isGoogleCatalystLyraAiEnabled, providerEpisodes],
  )
  const clientContext = useMemo(
    () =>
      buildLaunchDarklyClientContext({
        clientId,
        country: clientCountry,
        displayLanguage: clientLanguage,
        effectiveGenAiOptOut: effectiveClientGenAiOptOut,
      }),
    [clientCountry, clientId, clientLanguage, effectiveClientGenAiOptOut],
  )
  useTwilioMessaging()
  useSetApiMetadata()

  const dispatch = useAppDispatch()
  const deviceContext: LDContext = useMemo(
    () => ({
      kind: 'device',
      /**
       * This device context is anonymous
       * The key is omitted, and the SDK will automatically generate one
       */
      ...(launchDarklyUserDeviceUUID && { key: launchDarklyUserDeviceUUID }),
    }),
    [],
  )
  const userContext: LDContext = useMemo(
    () => ({
      key: launchDarklyUserId,
      kind: 'user',
      roles,
      supervisorRoles,
      country,
      mosApproved,
      ...(supervisor?.id && { supervisorId: supervisor.id }),
    }),
    [country, mosApproved, roles, supervisorRoles, launchDarklyUserId, supervisor?.id],
  )

  useFetcher(
    [
      [getCustomers, {}, isLoggedIn],
      [getProviderCapacity, { id: userId }, !!config?.bcPlatform],
    ],
    [isLoggedIn, userId],
  )
  useFetcher(
    [
      [
        getCustomersByCountry,
        { country: clientCountry },
        isLoggedIn && clientCountry && !(clientCountry in customersByCountry),
      ],
    ],
    [isLoggedIn, clientCountry, customersByCountry],
  )

  useEffect(() => {
    if (webKeyData?.provider_id !== userId || supervisor) {
      return
    }
    dispatch(setScope({ id: userId, encryptionKey: webKeyData.web_key }))
  }, [dispatch, supervisor, userId, webKeyData?.provider_id, webKeyData?.web_key])

  useEffect(() => {
    if (userId) {
      Sentry.setUser({ id: userId })
      Sentry.getGlobalScope().setAttributes({
        is_supervisor: !!supervisor,
        roles: roles?.join(','),
        supervisor_roles: supervisorRoles?.join(','),
        country,
      })
    }
  }, [userId, supervisor, roles, country, supervisorRoles])

  const client = useLDClient()
  const [ldIdentification, setLdIdentification] = useState<{
    userId: string
    status: 'ready' | 'failed'
  }>()
  useEffect(() => {
    const identifiedUserId = String(userContext.key)
    let cancelled = false
    setLdIdentification((current) => (current?.userId === identifiedUserId ? current : undefined))

    const markLdIdentificationFailed = () => {
      if (cancelled) return

      setLdIdentification((current) =>
        current?.userId === identifiedUserId ? current : { userId: identifiedUserId, status: 'failed' },
      )
      // This flag means the identification attempt has completed, not necessarily succeeded.
      // Consumers must be allowed to fall back to LaunchDarkly defaults instead of loading forever.
      dispatch(setLdInitialized())
    }

    // Never let Pendo or other LaunchDarkly consumers wait forever. An SDK that never loads, or an
    // identify() that neither resolves nor rejects, falls back to default flag values after this.
    const timeoutId = setTimeout(markLdIdentificationFailed, LD_IDENTIFY_TIMEOUT_MS)
    const settle = () => {
      cancelled = true
      clearTimeout(timeoutId)
    }

    if (!client) {
      return settle
    }

    const multiContext: LDMultiKindContext = {
      kind: 'multi',
      user: userContext,
      device: deviceContext,
      application: {
        key: 'application',
        applicationName: 'providerportalui',
      },
    }
    if (clientContext) {
      multiContext.client = clientContext
    }
    console.debug(
      `Vendor - Context flags available for user: ${userContext.key}, device: ${launchDarklyUserDeviceUUID}`,
    )

    client
      .identify(multiContext)
      .then(() => {
        clearTimeout(timeoutId)
        if (!cancelled) {
          setLdIdentification({ userId: identifiedUserId, status: 'ready' })
          dispatch(setLdInitialized())
        }
      })
      .catch((error) => {
        clearTimeout(timeoutId)
        if (!cancelled) {
          console.warn('LaunchDarkly identify failed:', error)
        }
        markLdIdentificationFailed()
      })

    return settle
  }, [client, clientContext, userContext, deviceContext, dispatch])

  const requiresDAMigrationEvaluation = !!user && isEngageDAProvider(user.roles)
  const { isReady: isPendoReady, migrationValue } = resolvePendoDAMigrationEvaluation({
    userId,
    requiresDAMigrationEvaluation,
    ldIdentification,
    isDAProviderMigrated,
  })
  const pendoVisitor = useMemo(
    () =>
      user
        ? buildPendoProviderVisitor({
            user,
            environment:
              process.env.NODE_ENV === 'production'
                ? PENDO_ENVIRONMENT_IDENTIFIER.PRODUCTION
                : PENDO_ENVIRONMENT_IDENTIFIER.NON_PRODUCTION,
            isDAProviderMigrated: migrationValue,
          })
        : undefined,
    [migrationValue, user],
  )
  useProviderPendoVisitor({
    isLoggedIn,
    isReady: isPendoReady,
    visitor: pendoVisitor,
  })

  return <>{children}</>
}

type AppContainerProps = {
  actions: any
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
  actions: bindActionCreators(
    {
      getCustomers,
      getProviderCapacity,
      getCustomersByCountry,
    },
    dispatch,
  ),
})

export default connect(null, mapDispatchToProps)(AppContainer)
